import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utilities/common/cn';

// Typography variants using Class Variance Authority
const typographyVariants = cva('', {
  variants: {
    color: {
      default: 'text-gray-900',
      primary: 'text-blue-700',
      secondary: 'text-gray-600',
      success: 'text-green-600',
      error: 'text-red-600',
      warning: 'text-yellow-600',
      inherit: 'text-inherit',
    },
    variant: {
      // Display variants - using rem values for better accessibility
      'display-2xl':
        'text-[4.5rem] font-work-sans leading-[1.2] tracking-[-0.02em]',
      'display-xl':
        'text-[3.75rem] font-work-sans leading-[1.2] tracking-[-0.02em]',
      'display-lg':
        'text-[3rem] font-work-sans leading-[1.2] tracking-[-0.01em]',
      'display-md':
        'text-[2.25rem] font-work-sans leading-[1.2] tracking-[-0.01em]',
      'display-sm':
        'text-[1.625rem] leading-[1.2] tracking-[-0.01em] font-work-sans',
      'display-xs':
        'text-[1.5rem] leading-[1.2] tracking-[-0.01em] font-figtree',

      // Heading variants
      h1: 'text-[2rem] leading-[1.25] tracking-[-0.01em] font-figtree',
      h2: 'text-[1.75rem] leading-[1.25] tracking-[-0.01em] font-figtree',
      h3: 'text-[1.5rem] leading-[1.25] tracking-[-0.01em] font-figtree',
      h4: 'text-[1.25rem] leading-[1.25] tracking-[-0.01em] font-figtree',
      h5: 'text-[1.125rem] leading-[1.25] tracking-[-0.01em] font-figtree',
      h6: 'text-[1rem] leading-[1.25] tracking-[-0.01em] font-figtree',

      // Body variants
      'body-xl': 'text-[1.25rem] font-figtree leading-[1.5] tracking-[0] ',
      'body-lg': 'text-[1.125rem] font-figtree leading-[1.5] tracking-[0]',
      'body-md': 'text-[1rem] font-figtree leading-[1.5] tracking-[0]',
      'body-sm': 'text-[0.875rem] font-figtree leading-[1.5] tracking-[0]',
      'body-xs': 'text-[0.75rem] font-figtree leading-[1.5] tracking-[0]',

      // Label variants
      'label-lg':
        'text-[1rem] font-figtree leading-[1.25] tracking-[0.01em] font-medium',
      'label-md':
        'text-[0.875rem] font-figtree leading-[1.25] tracking-[0.01em] font-medium',
      'label-sm':
        'text-[0.75rem] font-figtree leading-[1.25] tracking-[0.01em] font-medium',
      'label-xs':
        'text-[0.625rem] font-figtree leading-[1.25] tracking-[0.01em] font-medium',

      // Caption variants
      'caption-lg': 'text-[0.875rem] font-figtree leading-[1.25] tracking-[0]',
      'caption-md': 'text-[0.75rem] font-figtree leading-[1.25] tracking-[0]',
      'caption-sm': 'text-[0.625rem] font-figtree leading-[1.25] tracking-[0]',
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    },
    decoration: {
      none: 'no-underline',
      underline: 'underline',
      'line-through': 'line-through',
    },
    transform: {
      none: 'normal-case',
      uppercase: 'uppercase',
      lowercase: 'lowercase',
      capitalize: 'capitalize',
    },
  },
  defaultVariants: {
    variant: 'body-md',
    weight: 'regular',
    align: 'left',
    decoration: 'none',
    transform: 'none',
    color: 'default',
  },
});

// Typography component props
export interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    VariantProps<typeof typographyVariants> {
  as?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'p'
    | 'span'
    | 'div'
    | 'label'
    | 'caption';
  children?: React.ReactNode;
  truncate?: boolean;
  noWrap?: boolean;
  // Accessibility props
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  role?: string;
  tabIndex?: number;
}

// Typography component
export const Typography = React.forwardRef<HTMLSpanElement, TypographyProps>(
  (
    {
      className,
      variant,
      weight,
      align,
      decoration,
      transform,
      color,
      as,
      children,
      truncate = false,
      noWrap = false,
      id,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedby,
      'aria-labelledby': ariaLabelledby,
      role,
      tabIndex,
      ...props
    },
    ref,
  ) => {
    // Determine the HTML element based on variant and as prop
    const getElement = (): keyof React.JSX.IntrinsicElements => {
      if (as) return as;

      switch (variant) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return variant;
        case 'label-lg':
        case 'label-md':
        case 'label-sm':
        case 'label-xs':
          return 'label';
        case 'caption-lg':
        case 'caption-md':
        case 'caption-sm':
          return 'caption';
        default:
          return 'p';
      }
    };

    const Element = getElement();

    // Accessibility considerations
    const accessibilityProps = {
      ...(id && { id }),
      ...(ariaLabel && { 'aria-label': ariaLabel }),
      ...(ariaDescribedby && { 'aria-describedby': ariaDescribedby }),
      ...(ariaLabelledby && { 'aria-labelledby': ariaLabelledby }),
      ...(role && { role }),
      ...(tabIndex !== undefined && { tabIndex }),
    };

    return React.createElement(
      Element,
      {
        className: cn(
          typographyVariants({
            variant,
            weight,
            align,
            decoration,
            transform,
            color,
          }),
          truncate && 'truncate',
          noWrap && 'whitespace-nowrap',
          // Ensure minimum touch target size for interactive elements
          role && 'min-h-[44px] min-w-[44px]',
          className,
        ),
        ref,
        ...accessibilityProps,
        ...props,
      },
      children,
    );
  },
);

Typography.displayName = 'Typography';

// Convenience components for common use cases
export const Display2xl = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="display-2xl" {...props} />);
Display2xl.displayName = 'Display2xl';

export const DisplayXl = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="display-xl" {...props} />);
DisplayXl.displayName = 'DisplayXl';

export const DisplayLg = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="display-lg" {...props} />);
DisplayLg.displayName = 'DisplayLg';

export const DisplayMd = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="display-md" {...props} />);
DisplayMd.displayName = 'DisplayMd';

export const DisplaySm = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="display-sm" {...props} />);
DisplaySm.displayName = 'DisplaySm';

export const H1 = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h1" {...props} />);
H1.displayName = 'H1';

export const H2 = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h2" {...props} />);
H2.displayName = 'H2';

export const H3 = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h3" {...props} />);
H3.displayName = 'H3';

export const H4 = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h4" {...props} />);
H4.displayName = 'H4';

export const H5 = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h5" {...props} />);
H5.displayName = 'H5';

export const H6 = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h6" {...props} />);
H6.displayName = 'H6';

export const BodyXl = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="body-xl" {...props} />);
BodyXl.displayName = 'BodyXl';

export const BodyLg = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="body-lg" {...props} />);
BodyLg.displayName = 'BodyLg';

export const BodyMd = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="body-md" {...props} />);
BodyMd.displayName = 'BodyMd';

export const BodySm = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="body-sm" {...props} />);
BodySm.displayName = 'BodySm';

export const BodyXs = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="body-xs" {...props} />);
BodyXs.displayName = 'BodyXs';

export const LabelLg = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="label-lg" {...props} />);
LabelLg.displayName = 'LabelLg';

export const LabelMd = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="label-md" {...props} />);
LabelMd.displayName = 'LabelMd';

export const LabelSm = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="label-sm" {...props} />);
LabelSm.displayName = 'LabelSm';

export const LabelXs = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="label-xs" {...props} />);
LabelXs.displayName = 'LabelXs';

export const CaptionLg = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="caption-lg" {...props} />);
CaptionLg.displayName = 'CaptionLg';

export const CaptionMd = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="caption-md" {...props} />);
CaptionMd.displayName = 'CaptionMd';

export const CaptionSm = React.forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="caption-sm" {...props} />);
CaptionSm.displayName = 'CaptionSm';

// Export types
export type { VariantProps } from 'class-variance-authority';
